<template>
  <div>
    <PageHeader title="Import Contacts" showBack />

    <!-- Tab selector -->
    <b-tabs content-class="mt-3">
      <!-- CSV Import Tab -->
      <b-tab title="CSV Import" active>
        <b-card>
          <!-- Step 1: File Upload -->
          <div v-if="csvStep === 1">
            <h5>Step 1: Upload CSV File</h5>
            <p class="text-muted">Upload a CSV file with contact data. The first row should contain column headers.</p>
            <b-form-file v-model="csvFile" accept=".csv" placeholder="Choose a CSV file or drop it here..." drop-placeholder="Drop file here..." @input="onFileSelected" />
            <div v-if="csvFile" class="mt-3">
              <b-button variant="primary" @click="previewCSV" :disabled="isPreviewing">
                {{ isPreviewing ? 'Parsing...' : 'Next: Map Fields' }}
              </b-button>
            </div>
          </div>

          <!-- Step 2: Field Mapping -->
          <div v-if="csvStep === 2">
            <h5>Step 2: Map CSV Columns to Contact Fields</h5>
            <p class="text-muted">{{ csvTotalRows }} rows found. Map each CSV column to the corresponding contact field.</p>
            <b-table-simple responsive bordered small class="mb-3">
              <b-thead>
                <b-tr>
                  <b-th>CSV Column</b-th>
                  <b-th>Contact Field</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="header in csvHeaders" :key="header">
                  <b-td>{{ header }}</b-td>
                  <b-td>
                    <b-form-select v-model="fieldMapping[header]" :options="contactFieldOptions" size="sm" />
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <div class="d-flex">
              <b-button variant="outline-secondary" class="mr-2" @click="csvStep = 1">Back</b-button>
              <b-button variant="primary" @click="csvStep = 3">Next: Preview</b-button>
            </div>
          </div>

          <!-- Step 3: Preview -->
          <div v-if="csvStep === 3">
            <h5>Step 3: Preview</h5>
            <p class="text-muted">Showing first {{ csvPreviewRows.length }} of {{ csvTotalRows }} rows with your mapping applied.</p>
            <b-table :items="mappedPreview" :fields="mappedFields" responsive bordered small class="mb-3" />
            <div class="d-flex">
              <b-button variant="outline-secondary" class="mr-2" @click="csvStep = 2">Back</b-button>
              <b-button variant="primary" @click="importCSV" :disabled="isImportingCSV">
                {{ isImportingCSV ? 'Importing...' : 'Import Contacts' }}
              </b-button>
            </div>
          </div>

          <!-- Step 4: Results -->
          <div v-if="csvStep === 4">
            <h5>Import Results</h5>
            <b-alert show :variant="importResults.errors > 0 ? 'warning' : 'success'">
              <strong>{{ importResults.created }}</strong> contacts created,
              <strong>{{ importResults.skipped }}</strong> skipped (duplicates),
              <strong>{{ importResults.errors }}</strong> errors
            </b-alert>
            <div v-if="importResults.error_details && importResults.error_details.length > 0">
              <h6>Error Details:</h6>
              <ul class="small text-danger">
                <li v-for="(err, idx) in importResults.error_details" :key="idx">{{ err }}</li>
              </ul>
            </div>
            <b-button variant="primary" :to="{ name: 'contacts-list' }">Back to Contacts</b-button>
            <b-button variant="outline-secondary" class="ml-2" @click="resetCSV">Import Another</b-button>
          </div>
        </b-card>
      </b-tab>

      <!-- API Import Tab -->
      <b-tab title="System Import">
        <b-card>
          <h5>Select Import Source</h5>
          <p class="text-muted">Import contacts from your other Transition systems.</p>

          <b-row class="mt-4">
            <b-col md="4">
              <b-card class="text-center h-100 cursor-pointer" :class="{ 'border-primary': source === 'transition' }" @click="source = 'transition'">
                <i class="material-icons mb-2" style="font-size: 48px; color: #4e73df;">school</i>
                <h6>Transition Platform</h6>
                <p class="text-muted small">Import students and employers from your main Transition platform.</p>
              </b-card>
            </b-col>
            <b-col md="4">
              <b-card class="text-center h-100 cursor-pointer" :class="{ 'border-primary': source === 'enroll' }" @click="source = 'enroll'">
                <i class="material-icons mb-2" style="font-size: 48px; color: #1cc88a;">how_to_reg</i>
                <h6>TransitionEnroll</h6>
                <p class="text-muted small">Import students and inquiries from TransitionEnroll.</p>
              </b-card>
            </b-col>
          </b-row>

          <div v-if="source" class="mt-4">
            <b-button variant="primary" @click="startImport" :disabled="isImporting">
              {{ isImporting ? 'Importing...' : `Import from ${source === 'transition' ? 'Transition' : 'TransitionEnroll'}` }}
            </b-button>
          </div>

          <b-alert v-if="importMessage" :variant="importVariant" show class="mt-3">{{ importMessage }}</b-alert>
        </b-card>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

const CONTACT_FIELD_OPTIONS = [
  { value: '', text: '-- Skip --' },
  { value: 'first_name', text: 'First Name' },
  { value: 'last_name', text: 'Last Name' },
  { value: 'email', text: 'Email' },
  { value: 'phone', text: 'Phone' },
  { value: 'contact_type', text: 'Type' },
  { value: 'address', text: 'Address' },
  { value: 'city', text: 'City' },
  { value: 'state', text: 'State' },
  { value: 'zipcode', text: 'Zipcode' },
  { value: 'notes', text: 'Notes' },
];

export default {
  name: 'ImportContacts',
  components: { PageHeader },
  data() {
    return {
      // CSV import state
      csvStep: 1,
      csvFile: null,
      csvHeaders: [],
      csvPreviewRows: [],
      csvTotalRows: 0,
      fieldMapping: {},
      isPreviewing: false,
      isImportingCSV: false,
      importResults: {},
      contactFieldOptions: CONTACT_FIELD_OPTIONS,
      // API import state
      source: '',
      isImporting: false,
      importMessage: '',
      importVariant: 'info',
    };
  },
  computed: {
    mappedFields() {
      const fields = [];
      for (const [csv, contact] of Object.entries(this.fieldMapping)) {
        if (contact) {
          fields.push({ key: contact, label: `${contact} (${csv})` });
        }
      }
      return fields;
    },
    mappedPreview() {
      return this.csvPreviewRows.map(row => {
        const mapped = {};
        for (const [csv, contact] of Object.entries(this.fieldMapping)) {
          if (contact && row[csv] !== undefined) {
            mapped[contact] = row[csv];
          }
        }
        return mapped;
      });
    },
  },
  methods: {
    ...mapActions('contact', ['importContacts', 'csvPreview', 'csvImport']),
    onFileSelected() {
      if (this.csvFile) {
        this.csvStep = 1;
      }
    },
    async previewCSV() {
      this.isPreviewing = true;
      try {
        const formData = new FormData();
        formData.append('file', this.csvFile);
        const res = await this.csvPreview(formData);
        const data = res.data || res;
        this.csvHeaders = data.headers || [];
        this.csvPreviewRows = data.preview || [];
        this.csvTotalRows = data.total_rows || 0;

        // Auto-map fields by matching header names
        this.fieldMapping = {};
        const fieldNames = CONTACT_FIELD_OPTIONS.map(f => f.value).filter(Boolean);
        for (const header of this.csvHeaders) {
          const normalized = header.toLowerCase().replace(/[^a-z]/g, '_');
          const match = fieldNames.find(f => f === normalized || normalized.includes(f));
          this.fieldMapping[header] = match || '';
        }

        this.csvStep = 2;
      } catch (err) {
        this.makeToast('danger', 'Failed to parse CSV file');
      } finally {
        this.isPreviewing = false;
      }
    },
    async importCSV() {
      this.isImportingCSV = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const formData = new FormData();
        formData.append('file', this.csvFile);
        formData.append('field_mapping', JSON.stringify(this.fieldMapping));
        if (user.school) {
          formData.append('school_id', user.school.id);
        }
        const res = await this.csvImport(formData);
        this.importResults = res.data || res;
        this.csvStep = 4;
      } catch (err) {
        this.makeToast('danger', 'Import failed');
      } finally {
        this.isImportingCSV = false;
      }
    },
    resetCSV() {
      this.csvStep = 1;
      this.csvFile = null;
      this.csvHeaders = [];
      this.csvPreviewRows = [];
      this.csvTotalRows = 0;
      this.fieldMapping = {};
      this.importResults = {};
    },
    async startImport() {
      this.isImporting = true;
      this.importMessage = '';
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        await this.importContacts({ source: this.source, school_id: user.school.id });
        this.importMessage = 'Import started! Contacts will appear shortly.';
        this.importVariant = 'success';
      } catch (err) {
        this.importMessage = 'Import failed. Please try again.';
        this.importVariant = 'danger';
      } finally {
        this.isImporting = false;
      }
    },
  },
};
</script>
